import { exportFileName } from '@better-lights/core';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft, ChevronRight, Download, FolderInput, PenLine, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAccount } from '../account/store.ts';
import { backupProjects, confirmDeleteProject, deleteProjectFile, moveProjectFlow } from '../app/projectActions.ts';
import { useBlobUrl } from '../app/useBlobUrl.ts';
import { formatDate, plural, timeAgo } from '../lib/format.ts';
import { QuotePanel } from '../quote/QuotePanel.tsx';
import type { FilesFilter, ProjectTab } from '../router.tsx';
import { useLibrary } from '../state/library.ts';
import { storage } from '../storage/index.ts';
import { type ProjectFile, projectTitle } from '../storage/record.ts';
import { Button } from '../ui/Button.tsx';
import { Field } from '../ui/Field.tsx';
import { FileViewer, type ViewerItem } from '../ui/FileViewer.tsx';
import { IconButton } from '../ui/IconButton.tsx';
import { Segmented } from '../ui/Segmented.tsx';

const TIME_LABEL = { day: 'Day', evening: 'Evening', night: 'Night' } as const;

function itemsOf(p: ProjectFile): ViewerItem[] {
  const ai: ViewerItem[] = p.aiResults.map((a) => ({
    id: a.id,
    kind: 'ai',
    uri: a.uri,
    title: `${TIME_LABEL[a.time]} render`,
    createdAt: a.createdAt,
    fileName: exportFileName('ai', a.time, new Date(a.createdAt)),
  }));
  const ex: ViewerItem[] = p.exports.map((e) => ({
    id: e.id,
    kind: 'export',
    uri: e.uri,
    title: e.kind === 'demo' ? 'Demo export' : 'Lines export',
    createdAt: e.createdAt,
    fileName: exportFileName(e.kind, undefined, new Date(e.createdAt)),
  }));
  return [...ai, ...ex].sort((a, b) => b.createdAt - a.createdAt);
}

function Name({ project, startEditing }: { project: ProjectFile; startEditing: boolean }) {
  const [editing, setEditing] = useState(startEditing);
  const [text, setText] = useState(project.name ?? '');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(() => {
      input.current?.focus();
      input.current?.select();
    }, 300);
    return () => clearTimeout(t);
  }, [editing]);
  const commit = async () => {
    await storage().projects.rename(project.id, text);
    setEditing(false);
  };
  const caption = project.name
    ? `Started ${formatDate(project.createdAt)} · edited ${timeAgo(project.updatedAt)}`
    : `Edited ${timeAgo(project.updatedAt)}`;
  if (editing) {
    return (
      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void commit();
        }}
      >
        <Field
          ref={input}
          aria-label="Project name"
          className="flex-1"
          value={text}
          maxLength={60}
          autoCapitalize="words"
          placeholder={formatDate(project.createdAt)}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            if (text.trim() !== (project.name ?? '')) void commit();
          }}
        />
        <Button size="sm" type="submit">
          Done
        </Button>
      </form>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="flex items-center gap-2 text-left"
        onClick={() => setEditing(true)}
        aria-label={`Rename ${projectTitle(project)}`}
      >
        <h1 className="t-display line-clamp-2">{projectTitle(project)}</h1>
        <PenLine size={18} className="shrink-0 text-muted" aria-hidden />
      </button>
      <span className="t-caption text-dim">{caption}</span>
    </div>
  );
}

function Thumb({ item, onOpen }: { item: ViewerItem; onOpen: () => void }) {
  const url = useBlobUrl(item.uri);
  const Glyph = item.kind === 'ai' ? Sparkles : Download;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative aspect-square overflow-hidden bg-surface2"
      aria-label={item.title}
    >
      {url ? <img src={url} alt="" className="size-full object-cover" /> : null}
      <span className="absolute bottom-1 left-1 flex size-[18px] items-center justify-center rounded-xs bg-black/45">
        <Glyph size={12} aria-hidden />
      </span>
    </button>
  );
}

export function ProjectScreen() {
  const { projectId } = useParams({ from: '/p/$projectId' });
  const search = useSearch({ from: '/p/$projectId' });
  const navigate = useNavigate();
  const { projects, ready } = useLibrary();
  const project = projects.find((p) => p.id === projectId);
  const photo = useBlobUrl(project?.photo.uri);
  const canMove = useAccount((a) => !!a.session && (a.me?.orgs.length ?? 0) > 0);
  const [tab, setTab] = useState<ProjectTab>(search.tab ?? 'overview');
  const [filter, setFilter] = useState<FilesFilter>(search.filter ?? 'all');
  const [viewer, setViewer] = useState<number | null>(null);
  const items = useMemo(() => (project ? itemsOf(project) : []), [project]);
  const shown = items.filter((i) => filter === 'all' || (filter === 'ai' ? i.kind === 'ai' : i.kind === 'export'));

  useEffect(() => {
    if (ready && !project) void navigate({ to: '/', replace: true });
  }, [ready, project, navigate]);
  if (!project) return <div className="min-h-dvh bg-bg" />;

  const goFiles = (f: FilesFilter) => {
    setFilter(f);
    setTab('files');
  };
  const back = () => (history.length > 1 ? history.back() : void navigate({ to: '/' }));
  const counts = {
    all: plural(shown.length, 'file'),
    ai: plural(shown.length, 'render'),
    exports: plural(shown.length, 'export'),
  };

  return (
    <main
      className="min-h-dvh"
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'calc(var(--safe-bottom) + 32px)' }}
    >
      <div className="flex min-h-11 items-center px-2">
        <IconButton icon={ArrowLeft} label="Projects" onClick={back} />
      </div>
      <div className="mx-auto flex max-w-[1120px] flex-col gap-4 p-4">
        <Name project={project} startEditing={!!search.rename} />
        <Segmented
          label="Project view"
          size="sm"
          className="mx-auto w-full max-w-[320px]"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'overview', label: 'Overview' },
            { value: 'files', label: 'Files' },
            { value: 'quote', label: 'Quote' },
          ]}
        />
        {tab === 'quote' ? (
          <div className="mx-auto w-full max-w-[640px]">
            <QuotePanel projectId={project.id} />
          </div>
        ) : tab === 'overview' ? (
          <div className="flex flex-col gap-4 min-[900px]:flex-row">
            <div className="min-w-0 flex-1">
              {photo ? (
                <img
                  src={photo}
                  alt="The house"
                  className="w-full rounded-sm object-cover"
                  style={{
                    aspectRatio: `${project.photo.width} / ${project.photo.height}`,
                    maxHeight: 'max(160px, min(560px, calc(100dvh - 430px)))',
                  }}
                />
              ) : (
                <div className="aspect-[4/3] w-full rounded-sm bg-surface2" />
              )}
            </div>
            <div className="flex flex-col gap-4 min-[900px]:w-80">
              <div className="flex flex-col divide-y divide-border overflow-hidden rounded-md bg-surface">
                <div className="flex min-h-11 items-center justify-between px-4">
                  <span className="t-body">Light runs</span>
                  <span className="t-body text-dim">{project.doc.runs.length}</span>
                </div>
                <button
                  type="button"
                  className="flex min-h-11 items-center justify-between px-4 hover:bg-surface2"
                  onClick={() => goFiles('ai')}
                >
                  <span className="t-body">AI renders</span>
                  <span className="t-body flex items-center gap-1 text-dim">
                    {project.aiResults.length} <ChevronRight size={16} aria-hidden />
                  </span>
                </button>
                <button
                  type="button"
                  className="flex min-h-11 items-center justify-between px-4 hover:bg-surface2"
                  onClick={() => goFiles('exports')}
                >
                  <span className="t-body">Exports</span>
                  <span className="t-body flex items-center gap-1 text-dim">
                    {project.exports.length} <ChevronRight size={16} aria-hidden />
                  </span>
                </button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  icon={PenLine}
                  className="flex-1"
                  title="Opens the project in the editor"
                  onClick={() => void navigate({ to: '/p/$projectId/edit', params: { projectId } })}
                >
                  Edit
                </Button>
                <Button
                  variant="danger"
                  icon={Trash2}
                  onClick={async () => {
                    if (await confirmDeleteProject(project)) back();
                  }}
                >
                  Delete
                </Button>
              </div>
              {canMove ? (
                <Button variant="ghost" size="sm" icon={FolderInput} onClick={() => void moveProjectFlow(project.id)}>
                  Move to another workspace
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                icon={Download}
                onClick={() =>
                  void backupProjects(
                    [project],
                    `better-lights-${projectTitle(project)
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '-')}`,
                  )
                }
              >
                Back up this project
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-4">
              {(['all', 'ai', 'exports'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`t-label ${filter === f ? 'font-semibold text-text' : 'text-muted'}`}
                  aria-pressed={filter === f}
                >
                  {f === 'all' ? 'All' : f === 'ai' ? 'AI Renders' : 'Exports'}
                </button>
              ))}
              <span className="t-caption ml-auto text-dim">{counts[filter]}</span>
            </div>
            {shown.length === 0 ? (
              <div className="flex flex-col items-center gap-1 py-16 text-center">
                <span className="t-heading">
                  {filter === 'ai' ? 'No AI Renders' : filter === 'exports' ? 'No Exports' : 'No Files'}
                </span>
                <span className="t-caption text-dim">
                  {filter === 'exports'
                    ? 'Images you export from the editor appear here.'
                    : 'Renders from the AI tab and images you export appear here.'}
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-0.5 min-[632px]:grid-cols-5 min-[932px]:grid-cols-6">
                {shown.map((it, i) => (
                  <Thumb key={it.id} item={it} onOpen={() => setViewer(i)} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <FileViewer
        items={shown}
        index={viewer}
        onIndex={setViewer}
        onClose={() => setViewer(null)}
        photoUri={project.photo.uri}
        onDelete={async (it) => {
          await deleteProjectFile(project.id, it.kind, it.id);
          setViewer(null);
        }}
      />
    </main>
  );
}
